import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RuntimeConfigService } from '../config/runtime-config.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private runtimeConfigService: RuntimeConfigService,
    private readonly authService: AuthService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.authService.ready()).pipe(
      switchMap(() => {
        const extraHeaders: Record<string, string> = {
          'X-App-Client': 'AECOM-RepGen',
          'X-Requested-With': 'XMLHttpRequest',
          'ocp-apim-subscription-key': this.runtimeConfigService.apimSubscriptionKey,
        };

        let correlationId = req.headers.get('X-Correlation-ID');

        if (!correlationId) {
          correlationId = sessionStorage.getItem('correlationId');
          if (!correlationId) {
            correlationId = crypto.randomUUID();
            sessionStorage.setItem('correlationId', correlationId);
          }
        }
        extraHeaders['X-Correlation-ID'] = correlationId;

        if (req.url.includes('file.core.windows')) {
          return next.handle(req);
        }

        if (!this.authService.hasAuthenticatedAccount()) {
          return next.handle(req.clone({ setHeaders: extraHeaders }));
        }

        const scope = this.runtimeConfigService.scopes ?? '';
        const scopes = [scope];
        return from(this.authService.acquireToken(scopes)).pipe(
          switchMap((token) =>
            this.dispatch(req, next, extraHeaders, token).pipe(
              catchError((error: unknown) =>
                this.recoverFromUnauthorized(error, req, next, extraHeaders, scopes),
              ),
            ),
          ),
        );
      }),
    );
  }

  private dispatch(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    extraHeaders: Record<string, string>,
    token: string | null,
  ): Observable<HttpEvent<unknown>> {
    const setHeaders = token
      ? { ...extraHeaders, Authorization: `Bearer ${token}` }
      : extraHeaders;
    return next.handle(req.clone({ setHeaders }));
  }

  // A 401/403 is treated as a possibly stale access token, not as a session termination. We attempt
  // a single forced silent renewal and retry. If the underlying session is genuinely unrenewable,
  // acquireToken initiates the supported interactive redirect; the user is never left silently
  // logged out on a transient failure.
  private recoverFromUnauthorized(
    error: unknown,
    req: HttpRequest<unknown>,
    next: HttpHandler,
    extraHeaders: Record<string, string>,
    scopes: string[],
  ): Observable<HttpEvent<unknown>> {
    const isAuthFailure =
      error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
    if (!isAuthFailure || !this.authService.hasAuthenticatedAccount()) {
      return throwError(() => error);
    }

    return from(
      this.authService.acquireToken(scopes, { forceRefresh: true, allowInteraction: true }),
    ).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => error);
        }
        return this.dispatch(req, next, extraHeaders, token);
      }),
    );
  }
}
